import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Error as ErrorIcon,
  Language,
  Security,
  Timer,
  Speed,
  Dns,
  Lock,
  Schedule
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme, alpha } from '@mui/material/styles';
import { Customer } from '../../types/exporter';
import { websiteApi } from '../../../../../api/cansense/websiteApi';
import { WebsiteTestResult, SSL_PORTS } from '../../types/website';

// ============================================================================
// Types
// ============================================================================

export interface AddWebsiteFormData {
  name: string;
  url: string;
  probeType: 'http_2xx' | 'ssl_cert';
  customerId: number | null;
  checkInterval: string;
  port?: number;
}

// Local UI-friendly interface that maps from API response
interface UrlTestResult {
  url: string;
  accessible: boolean;
  httpStatus?: number;
  responseTime?: number;
  isHttps?: boolean;
  sslInfo?: {
    issuer: string;
    validFrom: string;
    validTo: string;
    daysUntilExpiry: number;
  };
  dns?: {
    resolved_ip: string;
    resolution_time_ms: number;
  };
  redirectChain?: string[];
  error?: string;
}

// Map API response to local UI-friendly format
const mapApiTestResult = (apiResult: WebsiteTestResult, testedUrl: string): UrlTestResult => {
  let isHttps = false;
  try {
    const urlObj = new URL(testedUrl);
    isHttps = urlObj.protocol === 'https:';
  } catch {
    // Invalid URL, leave isHttps as false
  }
  return {
    url: testedUrl,
    accessible: apiResult.reachable ?? false,
    httpStatus: apiResult.status_code,
    responseTime: apiResult.response_time_ms,
    isHttps,
    sslInfo: apiResult.ssl ? {
      issuer: apiResult.ssl.issuer,
      validFrom: '',
      validTo: apiResult.ssl.expiry,
      daysUntilExpiry: apiResult.ssl.expiry_days,
    } : undefined,
    dns: apiResult.dns,
    redirectChain: apiResult.redirect_chain,
    error: apiResult.error,
  };
};

interface AddWebsiteDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: AddWebsiteFormData) => Promise<boolean>;
  customers?: Customer[];
  existingUrls?: string[];
}

// ============================================================================
// Constants
// ============================================================================

const CHECK_INTERVALS = [
  { value: '30s', label: '30 seconds', description: 'Most responsive, higher resource usage' },
  { value: '60s', label: '1 minute', description: 'Balanced monitoring (Recommended)' },
  { value: '2m', label: '2 minutes', description: 'Standard interval' },
  { value: '5m', label: '5 minutes', description: 'Lower resource usage' }
];

// ============================================================================
// Component
// ============================================================================

export const AddWebsiteDialog: React.FC<AddWebsiteDialogProps> = ({
  open,
  onClose,
  onAdd,
  customers = [],
  existingUrls = []
}) => {
  const theme = useTheme();

  // Form state
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [probeType, setProbeType] = useState<'http_2xx' | 'ssl_cert'>('http_2xx');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [checkInterval, setCheckInterval] = useState('60s');
  const [port, setPort] = useState<number>(443);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<UrlTestResult | null>(null);
  const [error, setError] = useState('');
  const [addAnywayChecked, setAddAnywayChecked] = useState(false);

  // Reset form when dialog closes
  const handleClose = () => {
    setName('');
    setUrl('');
    setProbeType('http_2xx');
    setCustomerId(null);
    setCheckInterval('60s');
    setPort(443);
    setTestResult(null);
    setError('');
    setAddAnywayChecked(false);
    onClose();
  };

  // Validate URL format
  const validateUrl = (value: string): boolean => {
    try {
      const urlObj = new URL(value);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Handle URL change
  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.target.value.trim();
    setUrl(newUrl);
    setTestResult(null);
    setError('');
    setAddAnywayChecked(false);

    if (newUrl && !validateUrl(newUrl)) {
      setError('Please enter a valid URL (including http:// or https://)');
    } else if (newUrl && existingUrls.includes(newUrl)) {
      setError('This URL is already being monitored');
    }

    // Auto-generate name from URL if not set
    if (!name && newUrl) {
      try {
        const urlObj = new URL(newUrl);
        setName(urlObj.hostname);
      } catch {
        // Invalid URL, don't auto-generate name
      }
    }
  };

  // Handle probe type change
  const handleProbeTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newProbeType: 'http_2xx' | 'ssl_cert' | null
  ) => {
    if (newProbeType !== null) {
      setProbeType(newProbeType);
      setTestResult(null);
      setAddAnywayChecked(false);
    }
  };

  // Test URL
  const handleTestUrl = async () => {
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL (including http:// or https://)');
      return;
    }

    setIsTesting(true);
    setError('');
    setTestResult(null);

    try {
      const apiResult = await websiteApi.testUrl(
        url,
        probeType,
        probeType === 'ssl_cert' ? port : undefined
      );

      // Map API response to local UI format
      const result = mapApiTestResult(apiResult, url);
      setTestResult(result);

      if (!result.accessible) {
        setError(result.error || 'URL is not accessible');
      }
    } catch (err) {
      setError('Failed to test URL connectivity');
      console.error('URL test failed:', err);
    } finally {
      setIsTesting(false);
    }
  };

  // Handle form submission
  const handleAdd = async () => {
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL');
      return;
    }

    if (!testResult?.accessible && !addAnywayChecked) {
      setError('Please test the URL first, or check "Add anyway"');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData: AddWebsiteFormData = {
        name: name || new URL(url).hostname,
        url,
        probeType,
        customerId,
        checkInterval,
        port: probeType === 'ssl_cert' ? port : undefined
      };

      const success = await onAdd(formData);
      if (success) {
        handleClose();
      } else {
        setError('Failed to add website');
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.data?.message || err?.response?.data?.message;
      setError(backendMessage || 'Failed to add website');
      console.error('Add website failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Check if form is valid
  const isFormValid = url && (addAnywayChecked || (!error && testResult?.accessible));

  // Get SSL expiry color
  const getSslExpiryColor = (days: number) => {
    if (days <= 7) return 'error';
    if (days <= 30) return 'warning';
    return 'success';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Add sx={{ mr: 1 }} />
          Add Website to Monitoring
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Probe Type Selection */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Monitoring Type
          </Typography>

          <ToggleButtonGroup
            value={probeType}
            exclusive
            onChange={handleProbeTypeChange}
            sx={{ mb: 3, width: '100%' }}
          >
            <ToggleButton
              value="http_2xx"
              sx={{
                flex: 1,
                py: 2,
                flexDirection: 'column',
                gap: 0.5,
                textTransform: 'none'
              }}
            >
              <Language />
              <Typography variant="body2" fontWeight="bold">
                HTTP/HTTPS Monitoring
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Check if website returns success (2xx) status
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="ssl_cert"
              sx={{
                flex: 1,
                py: 2,
                flexDirection: 'column',
                gap: 0.5,
                textTransform: 'none'
              }}
            >
              <Security />
              <Typography variant="body2" fontWeight="bold">
                SSL Certificate Monitoring
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Track certificate expiration and validity
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 3 }} />

          {/* URL Input Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Website Details
          </Typography>

          <TextField
            fullWidth
            label="Website URL"
            placeholder="https://example.com"
            value={url}
            onChange={handleUrlChange}
            error={!!error && !testResult}
            helperText={error && !testResult ? error : 'Enter the full URL including http:// or https://'}
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={handleTestUrl}
                  disabled={!url || isTesting || (!!error && !testResult)}
                  startIcon={isTesting ? <CircularProgress size={16} /> : <Language />}
                  size="small"
                  sx={{ ml: 1, minWidth: 100 }}
                >
                  {isTesting ? 'Testing...' : 'Test URL'}
                </Button>
              )
            }}
          />

          <TextField
            fullWidth
            label="Display Name"
            placeholder="e.g., Company Website"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Optional friendly name (defaults to hostname)"
            sx={{ mb: 3 }}
          />

          {/* Port field for SSL cert mode */}
          {probeType === 'ssl_cert' && (
            <FormControl sx={{ mb: 3, minWidth: 220 }}>
              <InputLabel>SSL Port</InputLabel>
              <Select
                value={port}
                onChange={(e) => {
                  setPort(e.target.value as number);
                  setTestResult(null);
                }}
                label="SSL Port"
              >
                {SSL_PORTS.map((p) => (
                  <MenuItem key={p.port} value={p.port}>
                    <Box>
                      <Typography variant="body2">{p.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Test Results */}
          {testResult && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: testResult.accessible
                  ? alpha(theme.palette.success.main, 0.05)
                  : alpha(theme.palette.error.main, 0.05),
                borderRadius: 1,
                border: `1px solid ${
                  testResult.accessible
                    ? alpha(theme.palette.success.main, 0.3)
                    : alpha(theme.palette.error.main, 0.3)
                }`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {testResult.accessible ? (
                  <CheckCircle sx={{ color: theme.palette.success.main, mr: 1 }} />
                ) : (
                  <ErrorIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                )}
                <Typography variant="subtitle2">
                  {testResult.accessible ? 'Connection Successful' : 'Connection Failed'}
                </Typography>
              </Box>

              {testResult.accessible && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {testResult.httpStatus && (
                    <Chip
                      label={`HTTP ${testResult.httpStatus}`}
                      color={testResult.httpStatus >= 200 && testResult.httpStatus < 300 ? 'success' : 'warning'}
                      size="small"
                    />
                  )}
                  {testResult.responseTime && (
                    <Chip
                      icon={<Speed sx={{ fontSize: '14px !important' }} />}
                      label={`${testResult.responseTime}ms`}
                      color={testResult.responseTime > 2000 ? 'warning' : 'success'}
                      size="small"
                    />
                  )}
                  <Chip
                    label={testResult.isHttps ? 'HTTPS' : 'HTTP'}
                    color={testResult.isHttps ? 'success' : 'warning'}
                    size="small"
                  />
                  {testResult.dns?.resolved_ip && (
                    <Chip
                      icon={<Dns sx={{ fontSize: '14px !important' }} />}
                      label={testResult.dns.resolved_ip}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              )}

              {/* SSL Certificate Info */}
              {testResult.sslInfo && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Lock sx={{ fontSize: 18, mr: 0.5, color: theme.palette.success.main }} />
                    SSL Certificate
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {testResult.sslInfo.issuer && (
                      <Chip label={`Issuer: ${testResult.sslInfo.issuer}`} variant="outlined" size="small" />
                    )}
                    <Chip
                      icon={<Schedule sx={{ fontSize: '14px !important' }} />}
                      label={`Expires in ${testResult.sslInfo.daysUntilExpiry} days`}
                      color={getSslExpiryColor(testResult.sslInfo.daysUntilExpiry)}
                      size="small"
                    />
                  </Box>
                </Box>
              )}

              {/* Redirect chain */}
              {testResult.redirectChain && testResult.redirectChain.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Redirects: {testResult.redirectChain.join(' → ')}
                  </Typography>
                </Box>
              )}

              {testResult.error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {testResult.error}
                </Alert>
              )}

              {!testResult.accessible && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={addAnywayChecked}
                      onChange={(e) => setAddAnywayChecked(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Add anyway (pending status -- will retry connection later)
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Configuration Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Monitoring Configuration
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Check Interval</InputLabel>
              <Select
                value={checkInterval}
                onChange={(e: SelectChangeEvent) => setCheckInterval(e.target.value)}
                label="Check Interval"
              >
                {CHECK_INTERVALS.map((interval) => (
                  <MenuItem key={interval.value} value={interval.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Timer sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2">
                          {interval.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {interval.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>How often to check the website</FormHelperText>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={customerId ?? ''}
                onChange={(e) => setCustomerId(e.target.value === '' ? null : Number(e.target.value))}
                label="Customer"
              >
                <MenuItem value="">
                  <em>Internal / No Customer</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.cust_id} value={customer.cust_id}>
                    {customer.cust_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Optional - assign to a customer</FormHelperText>
            </FormControl>
          </Box>

          {/* Info Alert */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> {probeType === 'http_2xx'
                ? 'HTTP monitoring will check if the website responds with a successful (2xx) status code.'
                : 'SSL monitoring will track certificate validity and alert before expiration.'}
              {' '}Monitoring begins within 30 seconds of adding.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleAdd}
          variant="contained"
          disabled={!isFormValid}
          loading={submitting}
          startIcon={<Add />}
        >
          Add Website
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddWebsiteDialog;
